import React, { useContext, useState, useEffect } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import { getAllUsers, updateUser, deleteUser, createUser } from '../api/users';
import './ucUsers.css';

const initialFormState = {
  username: '',
  email: '',
  password: '',
  role: 'user',
  status: 'active'
};

const getRoleLabel = (user) => {
  if (user?.isAdmin) return 'Admin';
  if (user?.role === 'moderator') return 'Moderator';
  if (user?.role === 'guest') return 'Guest';
  return 'User';
};

const getStatusValue = (user) => {
  if (user?.status) return user.status.toLowerCase();
  if (user?.isActive === false) return 'inactive';
  return 'active';
};

const UcUsers = () => {
  const { online } = useContext(GlobalContext);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    const response = await getAllUsers();
    const userList = Array.isArray(response?.Users)
      ? response.Users
      : Array.isArray(response?.users)
        ? response.users
        : [];

    if (userList.length) {
      setUsers(userList);
      setFilteredUsers(userList);
      setError(null);
    } else {
      setUsers([]);
      setFilteredUsers([]);
      setError('Failed to fetch users');
    }
    setLoading(false);
  };

  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter((user) => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return [user.email, user.username, fullName, user.firstName, user.lastName]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase().includes(searchTerm.toLowerCase()));
      });
    }

    if (filterRole !== 'all') {
      filtered = filtered.filter((user) => {
        const role = getRoleLabel(user).toLowerCase();
        return role === filterRole;
      });
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((user) => getStatusValue(user) === filterStatus);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, filterRole, filterStatus]);

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const resetForm = () => {
    setEditingUserId(null);
    setFormData(initialFormState);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setShowUserModal(true);
  };

  const handleOpenEditModal = (user) => {
    setEditingUserId(user._id || user.id);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '',
      role: user.isAdmin ? 'admin' : user.role || 'user',
      status: getStatusValue(user)
    });
    setShowUserModal(true);
  };

  const handleCloseModal = () => {
    setShowUserModal(false);
    resetForm();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    const response = await deleteUser(userId);
    if (response?.success !== false) {
      setUsers((prev) => prev.filter((user) => (user._id || user.id) !== userId));
      setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
    } else {
      alert(response?.message || 'Failed to delete user');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedUserIds.length) return;
    if (!window.confirm(`Delete ${selectedUserIds.length} selected user(s)?`)) return;

    setSubmitting(true);
    const results = await Promise.all(selectedUserIds.map((id) => deleteUser(id)));
    const hasFailures = results.some((response) => response?.success === false);

    if (!hasFailures) {
      setUsers((prev) => prev.filter((user) => !selectedUserIds.includes(user._id || user.id)));
      setSelectedUserIds([]);
      setError(null);
    } else {
      alert('One or more users could not be deleted');
    }
    setSubmitting(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    if (!formData.username.trim() || !formData.email.trim() || (!editingUserId && !formData.password.trim())) {
      alert('Username, email, and password are required');
      setSubmitting(false);
      return;
    }

    const payload = {
      username: formData.username.trim(),
      email: formData.email.trim(),
      password: formData.password.trim(),
      isActive: formData.status === 'active',
      isAdmin: formData.role === 'admin',
      accessLevel: formData.role === 'admin' ? 1 : formData.role === 'moderator' ? 2 : formData.role === 'guest' ? 3 : 4
    };

    if (editingUserId && !formData.password.trim()) {
      delete payload.password;
    }

    const response = editingUserId
      ? await updateUser(editingUserId, payload)
      : await createUser(payload);

    if (response?.success !== false) {
      const savedUser = response?.user || response?.data || response;
      const normalizedUser = {
        ...(savedUser || {}),
        _id: savedUser?._id || savedUser?.id || editingUserId,
        id: savedUser?.id || savedUser?._id || editingUserId,
        username: savedUser?.username || formData.username.trim(),
        email: savedUser?.email || formData.email.trim(),
        isActive: payload.isActive,
        isAdmin: payload.isAdmin,
        accessLevel: payload.accessLevel,
        role: formData.role,
        status: formData.status
      };

      if (editingUserId) {
        setUsers((prev) => prev.map((user) => ((user._id || user.id) === editingUserId ? normalizedUser : user)));
      } else {
        setUsers((prev) => [normalizedUser, ...prev]);
      }

      setShowUserModal(false);
      resetForm();
      setError(null);
    } else {
      alert(response?.message || 'Failed to save user');
    }

    setSubmitting(false);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUserIds(paginatedUsers.map((user) => user._id || user.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'badge-active';
      case 'inactive':
        return 'badge-inactive';
      case 'banned':
        return 'badge-banned';
      case 'suspended':
        return 'badge-suspended';
      case 'pending':
        return 'badge-pending';
      default:
        return 'badge-inactive';
    }
  };

  return (
    <div className="users-container">
      <div className="users-header">
        <div className="users-title">
          <h1>User Management</h1>
          <p>Manage all users in one place. Control access, assign roles, and monitor activity across your platform.</p>
        </div>
      </div>

      <div className="users-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        <div className="filter-group">
          <select className="filter-select" value={filterRole} onChange={(event) => setFilterRole(event.target.value)}>
            <option value="all">👤 Role</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="moderator">Moderator</option>
            <option value="guest">Guest</option>
          </select>

          <select className="filter-select" value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
            <option value="all">🏠 Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="banned">Banned</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div className="action-buttons">
          {selectedUserIds.length > 0 && (
            <button className="btn-export" onClick={handleDeleteSelected} disabled={submitting}>
              Delete Selected
            </button>
          )}
          <button className="btn-add-user" onClick={handleOpenCreateModal}>+ Add User</button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" onChange={handleSelectAll} checked={paginatedUsers.length > 0 && paginatedUsers.every((user) => selectedUserIds.includes(user._id || user.id))} />
                  </th>
                  <th>Full Name ⬍</th>
                  <th>Email ⬍</th>
                  <th>Username ⬍</th>
                  <th>Status ⬍</th>
                  <th>Role ⬍</th>
                  <th>Joined Date ⬍</th>
                  <th>Last Active ⬍</th>
                  <th>Actions ⬍</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length > 0 ? (
                  paginatedUsers.map((user) => {
                    const userId = user._id || user.id;
                    return (
                      <tr key={userId}>
                        <td>
                          <input type="checkbox" checked={selectedUserIds.includes(userId)} onChange={() => handleSelectUser(userId)} />
                        </td>
                        <td className="user-name-cell">
                          <img src={user.photo || `https://via.placeholder.com/32?text=${user.username?.charAt(0) || 'U'}`} alt={user.username} className="user-avatar" />
                          <span>{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}</span>
                        </td>
                        <td>{user.email}</td>
                        <td>{user.username}</td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeColor(getStatusValue(user))}`}>
                            {getStatusValue(user).charAt(0).toUpperCase() + getStatusValue(user).slice(1)}
                          </span>
                        </td>
                        <td>{getRoleLabel(user)}</td>
                        <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                        <td>{user.lastActive || '1 minute ago'}</td>
                        <td className="actions-cell">
                          <button className="action-btn edit-btn" title="Edit" onClick={() => handleOpenEditModal(user)}>
                            ✏️
                          </button>
                          <button className="action-btn delete-btn" title="Delete" onClick={() => handleDeleteUser(userId)}>
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="no-data">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="rows-per-page">
              <span>Rows per page</span>
              <select value={rowsPerPage} onChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>of {filteredUsers.length} rows</span>
            </div>

            <div className="pagination-controls">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>⟨⟨</button>
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>⟨</button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = index + 1;
                } else if (currentPage <= 3) {
                  pageNum = index + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + index;
                } else {
                  pageNum = currentPage - 2 + index;
                }
                return (
                  <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={currentPage === pageNum ? 'active' : ''}>
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span>...</span>
                  <button onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                </>
              )}

              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
                ⟩
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                ⟩⟩
              </button>
            </div>
          </div>
        </>
      )}

      {showUserModal && (
        <div className="product-modal-overlay" role="dialog" aria-modal="true">
          <div className="product-modal">
            <div className="product-modal-header">
              <h3>{editingUserId ? 'Edit User' : 'Add User'}</h3>
              <button className="product-modal-close" onClick={handleCloseModal} type="button" aria-label="Close dialog">
                ×
              </button>
            </div>

            <form className="product-form" onSubmit={handleSubmit}>
              <div className="product-form-grid">
                <label htmlFor="user-username">
                  Username
                  <input id="user-username" name="username" value={formData.username} onChange={handleInputChange} required />
                </label>

                <label htmlFor="user-email">
                  Email
                  <input id="user-email" type="email" name="email" value={formData.email} onChange={handleInputChange} required />
                </label>

                <label htmlFor="user-password">
                  Password
                  <input id="user-password" type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder={editingUserId ? 'Leave blank to keep current password' : ''} />
                </label>

                <label htmlFor="user-role">
                  Role
                  <select id="user-role" name="role" value={formData.role} onChange={handleInputChange}>
                    <option value="user">User</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                    <option value="guest">Guest</option>
                  </select>
                </label>

                <label htmlFor="user-status">
                  Status
                  <select id="user-status" name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
              </div>

              <div className="product-form-actions">
                <button type="button" className="btn-export" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-add-user" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UcUsers;

